import React from 'react';
import pathToRegexp from 'path-to-regexp';
import { Notice } from 'components/common/Notice';
import Loader from 'components/common/Loader';
import _ from 'common/underscore';
import moment from 'common/moment';
import Wahanda from 'common/wahanda';
import { EmployeeSalesReportAnalytics } from 'common/analytics';
import DISPLAY from '../displayStateEnum';
import Filters from '../common/Filters';
import { ReportTable } from './reportTable';
import ReportLayout from '../common/ReportLayout';
import ROUTES from '../SalesReportContainer/reportRoutes';

interface IEmployeeSalesReportProps extends React.HTMLAttributes<Element> {
  requestEmployeeSalesReportDataAction: (...args: any[]) => any;
  match: {
    params: {
      venueId: string;
      fromDate?: string;
      toDate?: string;
    };
  };
  history: {
    push: (...args: any[]) => any;
  };
  reportData?: {
    rows: {
      employee?: string;
      employeeId?: number;
      numberOfServices?: number;
      serviceGrossTotal?: number;
      numberOfProducts?: number;
      productGrossTotal?: number;
      numberOfVouchers?: number;
      voucherGrossTotal?: number;
      grossTotalLeftover?: number;
      paidBySalonVoucher?: number;
    }[];
    total: {
      numberOfServices?: number;
      serviceGrossTotal?: number;
      numberOfProducts?: number;
      productGrossTotal?: number;
      numberOfVouchers?: number;
      voucherGrossTotal?: number;
      grossTotalLeftover?: number;
      paidBySalonVoucher?: number;
    };
  };
  displayState: any;
  params?: any;
  push?: any;
}

export default class EmployeeSalesReport extends React.Component<IEmployeeSalesReportProps, {}> {
  static defaultProps = {
    reportData: null,
  };

  componentDidMount = () => {
    EmployeeSalesReportAnalytics.trackPageView();
    this.requestDataFromApi();
  };

  componentDidUpdate = (prevProps) => {
    if (!_.isEqual(this.props.match.params, prevProps.match.params)) {
      this.requestDataFromApi();
    }
  };

  onFilterChange = (filters) => {
    const { venueId } = this.getQueryParams();
    const { fromDate, toDate } = filters;
    const toPath = pathToRegexp.compile(ROUTES.EMPLOYEE_SALES);
    const newPath = toPath({
      venueId,
      fromDate: fromDate.formatApiDateString(),
      toDate: toDate.formatApiDateString(),
    });
    this.props.history.push(newPath);
  };

  getLoadingSpinner = () => <Loader />;

  getErrorMessage = () => <div>{Wahanda.lang.reports.sales.error}</div>;

  getQueryParams = () => {
    const { venueId, fromDate, toDate } = _.defaults(this.props.match.params, {
      fromDate: moment().startOf('month').formatApiDateString(),
      toDate: moment().endOf('month').formatApiDateString(),
    });
    return {
      venueId,
      fromDate: moment(fromDate),
      toDate: moment(toDate),
    };
  };

  trackDateFilterChange = (data) => {
    EmployeeSalesReportAnalytics.trackReportDateFilterChange(data.type);
  };

  requestDataFromApi = () => {
    const { venueId, fromDate, toDate } = this.getQueryParams();
    this.props.requestEmployeeSalesReportDataAction(venueId, fromDate, toDate);
  };

  render() {
    let body;
    switch (this.props.displayState) {
      case DISPLAY.WAITING:
        body = this.getLoadingSpinner();
        break;
      case DISPLAY.REPORT:
        body = (
          <ReportTable
            venueId={this.getQueryParams().venueId}
            // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
            reportData={this.props.reportData}
            fromDate={this.getQueryParams().fromDate}
            toDate={this.getQueryParams().toDate}
          />
        );
        break;
      case DISPLAY.ERROR:
        body = this.getErrorMessage();
        break;
      default:
        throw new Error(`Unrecognised display state: ${this.props.displayState}`);
    }
    const dateFilterProps = {
      uniqueId: 'employeeSalesReportDateFilter',
      fromDate: this.getQueryParams().fromDate,
      toDate: this.getQueryParams().toDate,
    };
    const filters = (
      <Filters
        date={dateFilterProps}
        onFilterChange={this.onFilterChange}
        onDatePickerChange={this.trackDateFilterChange}
      />
    );
    const messages = (
      <Notice type="success">
        <p
          dangerouslySetInnerHTML={{
            __html: Wahanda.lang.reports.newReportsMessage,
          }}
        />
      </Notice>
    );
    return (
      <ReportLayout
        messages={messages}
        filter={filters}
        showTimeStamp={this.props.displayState === DISPLAY.REPORT}
        body={body}
      />
    );
  }
}
